import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { Button } from '@/Components/UI/Button/Button';
import ComboBox from '@/Components/UI/Shadcn/ComboBox';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import { Select } from '@/Components/UI/Form/Select/Select';

import {
  changeIndividualColumnState,
  removeGroupedNodeState,
  updateGroupedNodeState,
} from '@/redux/reducers/spreadsheetSlice';

import { datasetSliceName, spreadsheetSliceName, userDataSliceName } from '@/redux/constants';

import { useCurrentFile } from '@/hooks/useCurrentFile';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';

const DEFAULT_COLUMN_OPTION = { value: '', field: '', label: '', type: '' };

export function GroupSection({
  title,
  groupData,
  unGroupedColumns,
  clientState,
  dateFunctionMap,
  addNewColumn,
  addGroupingByColumn,
  removeGroupingByColumn,
  addDateFunction,
  onDragEnd,
  isPivotEnabled,
}) {
  const dateFunctionOptions = [
    { value: '', label: 'Exact Date' },
    { value: 'year', label: 'Year' },
    { value: 'quarter', label: 'Quarter' },
    { value: 'month', label: 'Month' },
    { value: 'dayOfMonth', label: 'Day of Month' },
    { value: 'dayOfWeek', label: 'Day of Week' },
    { value: 'weekOfYear', label: 'Week of Year' },
    { value: 'hour', label: 'Hour' },
    { value: 'minute', label: 'Minute' },
    { value: 'second', label: 'Second' },
  ];

  const dispatch = useDispatch();

  const handleExpandAll = useCallback(
    (field) => {
      dispatch(
        updateGroupedNodeState({
          field,
          update: { defaultExpanded: true, collapsedNodes: [], expandedNodes: [] },
        })
      );
    },
    [dispatch, clientState]
  );

  const handleCollapseAll = useCallback(
    (field) => {
      dispatch(
        updateGroupedNodeState({
          field,
          update: { defaultExpanded: false, collapsedNodes: [], expandedNodes: [] },
        })
      );
    },
    [dispatch, clientState]
  );

  const groupType = title === 'Column Group' ? 'column' : 'row';

  return (
    <>
      {title === 'Row Group' && isPivotEnabled && (
        <h3 className='mb-2 text-lg font-semibold'>{title}</h3>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`${title}-droppable`}>
          {(provided) => (
            <div
              className='flex flex-col items-start pb-3 ml-2'
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {groupData.map((colObj, index) => {
                const columnState = clientState.groupedNodeState?.[colObj.field] || {};
                const { defaultExpanded, collapsedNodes = [], expandedNodes = [] } = columnState;

                const isExpandDisabled = defaultExpanded && collapsedNodes.length === 0;
                const isCollapseDisabled = !defaultExpanded && expandedNodes.length === 0;

                return (
                  <div key={`${groupType}-group-${index}`}>
                    <Draggable
                      key={`${groupType}-draggable-${index}`}
                      draggableId={`${groupType}-draggable-${index}`}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className='flex items-center w-full mb-3'
                        >
                          <div className='mr-3 w-[260px]'>
                            <ComboBox
                              options={unGroupedColumns}
                              dataCy={`group-by-${colObj.label}`}
                              selectionMode='single'
                              placeholder='Column'
                              onChange={(val) =>
                                addGroupingByColumn(val?.[0]?.value, colObj.field, index)
                              }
                              value={colObj.value ? [colObj] : []}
                            />
                          </div>

                          <div
                            className='flex items-center gap-2'
                            data-cy={`group-by-${groupType}-${index}`}
                          >
                            <p className='flex items-center font-overpass text-[15px] text-[#424242] cursor-grab before:content-moveGrabber before:h-[18px] before:w-[18px] before:mr-[3px] hover:before:content-moveGrabberHover' />

                            {colObj && colObj.field && title === 'Row Group' && (
                              <>
                                <Tooltip text='Expand All' side='top' asChild>
                                  <Button
                                    onClick={() => handleExpandAll(colObj.field)}
                                    color='oceanBlue'
                                    size='large'
                                    variant='outline'
                                    iconName='arrows-out-simple'
                                    dataCy='expand-all-button'
                                    disabled={isExpandDisabled}
                                  />
                                </Tooltip>
                                <Tooltip text='Collapse All' side='top' asChild>
                                  <Button
                                    onClick={() => handleCollapseAll(colObj.field)}
                                    color='oceanBlue'
                                    size='large'
                                    variant='outline'
                                    iconName='arrows-in-simple'
                                    dataCy='collapse-all-button'
                                    disabled={isCollapseDisabled}
                                  />
                                </Tooltip>
                              </>
                            )}

                            <Button
                              dataCy='remove-group-btn'
                              disabled={index === 0 && !colObj.field}
                              onClick={() => removeGroupingByColumn(colObj.field, index)}
                              color='shadow'
                              variant='ghost'
                              size='large'
                              iconName='x'
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>

                    {colObj.type === 'DateTime64' && (
                      <div className='flex w-full mb-3 ml-1'>
                        <div className='mr-3 text-sm text-gray-700 font-overpass'>
                          Date function
                        </div>
                        <div className='w-[130px]'>
                          <Select
                            size='small'
                            dataCy='group-by-date-function'
                            options={dateFunctionOptions}
                            value={dateFunctionMap[colObj.field] || dateFunctionOptions[0].value}
                            onChange={(value) => addDateFunction(value, colObj)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {provided.placeholder}

              <Button
                onClick={addNewColumn}
                size='small'
                variant='ghost'
                iconName='plus-circle'
                color='oceanBlue'
                className='mt-1 text-sm'
                dataCy='group-by-add-column-btn'
              >
                <span className='text-sm'>Add Group</span>
              </Button>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
}

export default function GroupBy() {
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();

  const {
    clientState: { isPivotEnabled },
    columnDefs,
    clientState,
  } = useSelector((state) => state[spreadsheetSliceName]);
  const { user } = useSelector((state) => state[userDataSliceName]);
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);
  const currentFile = useCurrentFile();

  const { fileName } = useSelector((state) => {
    const file = state[datasetSliceName]?.currentFile || {};
    return file?.metadata;
  }, shallowEqual);

  const [rowGroupData, setRowGroupData] = useState([DEFAULT_COLUMN_OPTION]);
  const [rowDateFunctionMap, setRowDateFunctionMap] = useState({});

  const [pivotGroupData, setPivotGroupData] = useState([DEFAULT_COLUMN_OPTION]);
  const [pivotDateFunctionMap, setPivotDateFunctionMap] = useState({});

  useEffect(() => {
    const newRowDateFuncs = {};
    const newPivotDateFuncs = {};

    clientState.columnState.forEach((col) => {
      if (col.groupFunc) {
        if (col.rowGroup) {
          newRowDateFuncs[col.colId] = col.groupFunc;
        }
        if (col.pivot) {
          newPivotDateFuncs[col.colId] = col.groupFunc;
        }
      }
    });

    setRowDateFunctionMap(newRowDateFuncs);
    setPivotDateFunctionMap(newPivotDateFuncs);
  }, [clientState.columnState]);

  const [groupedColumns, unGroupedColumns] = useMemo(() => {
    const grouped = [];
    const unGrouped = [];

    columnDefs.forEach((def) => {
      const colState = clientState.columnState.find((s) => s.colId === def.field);
      const columnData = {
        value: def.field,
        label: def.headerName,
        field: def.field,
        type: def.fieldType,
      };

      if (colState?.rowGroup) {
        grouped.push({ ...columnData, rowGroupIndex: colState.rowGroupIndex });
      } else {
        unGrouped.push(columnData);
      }
    });

    grouped.sort((a, b) => (a.rowGroupIndex ?? 0) - (b.rowGroupIndex ?? 0));
    return [grouped, unGrouped];
  }, [columnDefs, clientState.columnState]);

  const [pivotedColumns, unPivotedColumns] = useMemo(() => {
    const pivoted = [];
    const notPivoted = [];

    columnDefs.forEach((def) => {
      const colState = clientState.columnState.find((s) => s.colId === def.field);
      const columnData = {
        value: def.field,
        label: def.headerName,
        field: def.field,
        type: def.fieldType,
      };

      if (colState?.pivot) {
        pivoted.push({ ...columnData, pivotIndex: colState.pivotIndex });
      } else {
        notPivoted.push(columnData);
      }
    });

    pivoted.sort((a, b) => (a.pivotIndex ?? 0) - (b.pivotIndex ?? 0));
    return [pivoted, notPivoted];
  }, [columnDefs, clientState.columnState]);

  useEffect(() => {
    setRowGroupData(groupedColumns.length ? groupedColumns : [DEFAULT_COLUMN_OPTION]);
  }, [groupedColumns]);

  useEffect(() => {
    setPivotGroupData(pivotedColumns.length ? pivotedColumns : [DEFAULT_COLUMN_OPTION]);
  }, [pivotedColumns]);

  const onRowDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;
      if (!destination) return;

      const reordered = Array.from(rowGroupData);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);

      setRowGroupData(reordered);
      reordered.forEach((col, idx) => {
        dispatch(
          changeIndividualColumnState({
            colId: col.field,
            rowGroupIndex: idx,
            rowGroup: true,
            pivot: false,
            hide: true,
          })
        );
      });
    },
    [dispatch, rowGroupData]
  );

  const addNewRowGroup = useCallback(() => {
    const needsModal =
      (isPreviewMode && rowGroupData.length >= 1) ||
      ((!currentFile?.WithinQuota || currentFile?.OverRowQuota) && rowGroupData.length >= 1);

    if (needsModal) {
      return showPreviewModeUpsellModal('Add Additional Group Conditions on the Premium Plan');
    }

    if (rowGroupData.every((c) => c.field !== '')) {
      setRowGroupData((prev) => [...prev, DEFAULT_COLUMN_OPTION]);
    }
  }, [rowGroupData, dispatch, user, currentFile]);

  const addRowGroupingByColumn = useCallback(
    (newField, oldField, rowGroupIndex) => {
      if (oldField) {
        dispatch(
          changeIndividualColumnState({
            colId: oldField,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            hide: false,
          })
        );
      }

      if (newField) {
        dispatch(
          changeIndividualColumnState({
            colId: newField,
            rowGroup: true,
            rowGroupIndex,
            pivot: false,
            pivotIndex: null,
            hide: true,
          })
        );
      }
    },
    [dispatch]
  );

  const removeRowGroupingByColumn = useCallback(
    (colId, index) => {
      if (!colId) {
        setRowGroupData((prev) => prev.filter((_, i) => i !== index));
        return;
      }
      dispatch(
        changeIndividualColumnState({
          colId,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          hide: false,
        })
      );
      if (clientState.groupedNodeState?.[colId]) {
        dispatch(removeGroupedNodeState({ field: colId }));
      }
    },
    [dispatch, clientState.groupedNodeState]
  );

  const addRowDateFunction = useCallback(
    (value, columnObj) => {
      setRowDateFunctionMap((prev) => ({ ...prev, [columnObj.field]: value }));
      dispatch(
        changeIndividualColumnState({
          colId: columnObj.field,
          rowGroup: true,
          rowGroupIndex: columnObj.rowGroupIndex,
          pivot: false,
          pivotIndex: null,
          hide: true,
          groupFunc: value,
        })
      );
    },
    [dispatch]
  );

  const onPivotDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;
      if (!destination) return;

      const reordered = Array.from(pivotGroupData);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);

      setPivotGroupData(reordered);

      reordered.forEach((col, idx) => {
        dispatch(
          changeIndividualColumnState({
            colId: col.field,
            pivotIndex: idx,
            pivot: true,
            rowGroup: false,
            rowGroupIndex: null,
            hide: true,
          })
        );
      });
    },
    [dispatch, pivotGroupData]
  );

  const addNewPivotGroup = useCallback(() => {
    if (pivotGroupData.every((c) => c.field !== '')) {
      setPivotGroupData((prev) => [...prev, DEFAULT_COLUMN_OPTION]);
    }
  }, [pivotGroupData]);

  const addPivotGroupingByColumn = useCallback(
    (newField, oldField, pivotIndex) => {
      if (oldField) {
        dispatch(
          changeIndividualColumnState({
            colId: oldField,
            pivot: false,
            pivotIndex: null,
            rowGroup: false,
            rowGroupIndex: null,
            hide: false,
          })
        );
      }
      if (newField) {
        dispatch(
          changeIndividualColumnState({
            colId: newField,
            pivot: true,
            pivotIndex,
            rowGroup: false,
            rowGroupIndex: null,
            hide: true,
          })
        );
      }
    },
    [dispatch]
  );

  const removePivotGroupingByColumn = useCallback(
    (colId, index) => {
      if (!colId) {
        setPivotGroupData((prev) => prev.filter((_, i) => i !== index));
        return;
      }

      dispatch(
        changeIndividualColumnState({
          colId,
          pivot: false,
          pivotIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          hide: false,
        })
      );
    },
    [dispatch]
  );

  const addPivotDateFunction = useCallback(
    (value, columnObj) => {
      setPivotDateFunctionMap((prev) => ({ ...prev, [columnObj.field]: value }));
      dispatch(
        changeIndividualColumnState({
          colId: columnObj.field,
          pivot: true,
          pivotIndex: columnObj.pivotIndex,
          rowGroup: false,
          rowGroupIndex: null,
          hide: true,
          groupFunc: value,
        })
      );
    },
    [dispatch]
  );

  if (!isPivotEnabled) {
    return (
      <GroupSection
        title='Row Group'
        groupData={rowGroupData}
        unGroupedColumns={unGroupedColumns}
        clientState={clientState}
        dateFunctionMap={rowDateFunctionMap}
        addNewColumn={addNewRowGroup}
        addGroupingByColumn={addRowGroupingByColumn}
        removeGroupingByColumn={removeRowGroupingByColumn}
        addDateFunction={addRowDateFunction}
        onDragEnd={onRowDragEnd}
        isPivotEnabled={isPivotEnabled}
      />
    );
  }

  return (
    <div>
      <GroupSection
        title='Row Group'
        groupData={rowGroupData}
        unGroupedColumns={unGroupedColumns}
        clientState={clientState}
        dateFunctionMap={rowDateFunctionMap}
        addNewColumn={addNewRowGroup}
        addGroupingByColumn={addRowGroupingByColumn}
        removeGroupingByColumn={removeRowGroupingByColumn}
        addDateFunction={addRowDateFunction}
        onDragEnd={onRowDragEnd}
      />

      <hr className='my-4' />

      <GroupSection
        title='Column Group'
        groupData={pivotGroupData}
        unGroupedColumns={unPivotedColumns}
        clientState={clientState}
        dateFunctionMap={pivotDateFunctionMap}
        addNewColumn={addNewPivotGroup}
        addGroupingByColumn={addPivotGroupingByColumn}
        removeGroupingByColumn={removePivotGroupingByColumn}
        addDateFunction={addPivotDateFunction}
        onDragEnd={onPivotDragEnd}
      />
    </div>
  );
}
